import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import WorkLogEntry from "./WorkLogEntry";
import { ProjectContext } from "../../../AppContext/ProjectContext";
import { AuthContext } from "../../../AppContext/AuthContext";
import {
  getDailyReportsInRealTime,
  createDailyReport,
} from "../../../Config/db/daily-report.retrieve";
import { logProjectActivity } from "../../../Config/db/project-activity.operations";
import { Add, AssessmentOutlined, Send } from "@mui/icons-material";
import { v4 as uuid } from "uuid";
import MUIRichTextEditor from "mui-rte";
import { ThemeProvider } from "@mui/material/styles";
import { convertToRaw } from "draft-js";
import { editorTheme } from "../../../Shared/RHFRichEditor";
import { extractPlainText } from "../helper";

export default function WorkLog() {
  const [reports, setReports] = useState([]);
  const [note, setNote] = useState("");
  const [hasText, setHasText] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [taskInput, setTaskInput] = useState("");
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const { projectInfo } = useContext(ProjectContext);
  const { userInfo } = useContext(AuthContext);

  useEffect(() => {
    if (projectInfo?.id) {
      const unsub = getDailyReportsInRealTime(projectInfo.id, (data) => {
        setReports(data);
      });
      return () => unsub?.();
    } else {
      setReports([]);
    }
  }, [projectInfo?.id]);

  const addTask = () => {
    if (!taskInput.trim()) return;
    setTasks((prev) => [
      ...prev,
      {
        selfId: uuid(),
        taskName: taskInput.trim(),
        status: "open",
        estimate: "",
        priority: 1,
        outcomes: [{ outcomeName: "", status: "open" }],
      },
    ]);
    setTaskInput("");
  };

  const removeTask = (index) => {
    setTasks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Auto-add any pending task text so nothing is lost
    let finalTasks = [...tasks];
    if (taskInput.trim()) {
      finalTasks.push({
        selfId: uuid(),
        taskName: taskInput.trim(),
        status: "open",
        estimate: "",
        priority: 1,
        outcomes: [{ outcomeName: "", status: "open" }],
      });
      setTaskInput("");
    }
    if (!note.trim() && finalTasks.length === 0) return;
    setSaving(true);
    try {
      await createDailyReport(
        { report: note, actions: finalTasks },
        userInfo?.communityCode,
        projectInfo?.id
      );
      // Log activity
      const plainText = extractPlainText(note);
      logProjectActivity({
        projectId: projectInfo?.id,
        communityCode: userInfo?.communityCode,
        userId: userInfo?.uid,
        userName: userInfo?.displayName || "Unknown",
        type: "work_log",
        description: plainText
          ? `Logged work: "${plainText.substring(0, 80)}${plainText.length > 80 ? "…" : ""}"`
          : `Added ${finalTasks.length} follow-up task(s)`,
        meta: { taskCount: finalTasks.length },
      });
      setNote("");
      setHasText(false);
      setEditorKey((k) => k + 1);
      setTasks([]);
    } catch (err) {
      console.error("Failed to save work log:", err);
    } finally {
      setSaving(false);
    }
  };

  const canSave = hasText || tasks.length > 0 || taskInput.trim();

  return (
    <Box>
      {/* Inline create form */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid #e0e0e0" }}
      >
        <Box sx={{ mb: 1.5 }}>
          <ThemeProvider theme={editorTheme}>
            <MUIRichTextEditor
              key={editorKey}
              label="What progress was made?"
              onChange={(editorState) => {
                const currentContent = editorState.getCurrentContent();
                setHasText(currentContent.hasText());
                if (currentContent.hasText()) {
                  setNote(JSON.stringify(convertToRaw(currentContent)));
                } else {
                  setNote("");
                }
              }}
              controls={[
                "bold", "italic", "underline", "strikethrough",
                "highlight", "link", "numberList", "bulletList",
                "quote", "code",
              ]}
              inlineToolbar={true}
            />
          </ThemeProvider>
        </Box>

        {/* Task chips */}
        {tasks.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {tasks.map((task, i) => (
              <Chip
                key={task.selfId}
                label={task.taskName}
                onDelete={() => removeTask(i)}
                size="small"
                variant="outlined"
                sx={{ borderColor: "#1a237e", color: "#1a237e" }}
              />
            ))}
          </Box>
        )}

        {/* Add task + save row */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Add a follow-up task…"
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTask();
              }
            }}
            sx={{ flex: 1 }}
            InputProps={{
              endAdornment: taskInput.trim() ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={addTask}
                  startIcon={<Add sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: "none",
                    minWidth: "auto",
                    fontSize: "0.75rem",
                    color: "#1a237e",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Add
                </Button>
              ) : null,
            }}
            helperText={
              taskInput.trim()
                ? "Press Enter or click Add — tasks are also auto-included on Save"
                : undefined
            }
          />
          <Button
            variant="contained"
            size="small"
            disabled={!canSave || saving}
            onClick={handleSave}
            endIcon={<Send sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              color: "#fff",
              background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
              boxShadow: "0 2px 6px rgba(27,94,32,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #145218 0%, #256d29 100%)",
                boxShadow: "0 4px 10px rgba(27,94,32,0.45)",
              },
              "&.Mui-disabled": {
                background: "#bdbdbd",
                color: "#fff",
              },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Paper>

      {/* Work log entries */}
      {reports.length > 0 && (
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Previous Entries
        </Typography>
      )}

      {reports.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <AssessmentOutlined
            sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
          />
          <Typography color="text.secondary">
            No entries yet. Log work progress or add tasks above to get started.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={2}>
        {reports.map((report) => (
          <Grid item xs={12} key={report.id}>
            <WorkLogEntry report={report} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
